import {IVideoAnalyticsReporter} from "../../video/models/analytics/video-analytics-reporter.interface";
import {GameInfo} from "../../games/game-info";
import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {IVideoPlayerViewModel, IVideoResolution} from "../../video/models/video-player.view-model.interface";
import {VideoTypeEnum} from "../../video/models/video-type.enum";
import {
    IVideoSlidesAnalyticsData,
    SlideType,
    VideoSlidesAnalyticsEventEnum
} from "./video-slides-analytics-data.interface";
import {IStructuredLogger} from "../../../../common/interfaces/structured-logging-service-interface";
import {IVideoSwiperSlideController} from "../models/video-swiper-slide-controller.interface";
import {SwipeDirectionEnum} from "../../../components/spark-swiper/models/spark-swiper-slide-controller.interface";
import {TimeSpan} from "@sparkware/app-utils";

export class VideoSlidesAnalyticsReporter implements IVideoAnalyticsReporter {
    constructor(private readonly gameInfo: GameInfo,
                private readonly videoType: VideoTypeEnum,
                private readonly videoSlideController: IVideoSwiperSlideController,
                private readonly services: IServiceFactoryExtended) {
    }

    private get slideType(): SlideType {
        if(this.videoType === VideoTypeEnum.VideoStream) {
            return 'VideoStream';
        } else {
            return 'LiveStream';
        }
    }

    private get videoPlayer(): IVideoPlayerViewModel {
        return this.videoSlideController.videoPlayer;
    }

    private _roundTime(seconds: number): number {
        return Math.round(seconds * 100) / 100;
    }

    private _createLogger(eventData: Partial<IVideoSlidesAnalyticsData>): IStructuredLogger {

        const videoPlayer = this.videoPlayer;

        eventData = {
            slideType: this.slideType,
            gameType: this.gameInfo.gameType,
            gameName: this.gameInfo.title,
            videoWatchTime: this._roundTime(videoPlayer.watchTime.totalSeconds),
            isMuted: videoPlayer.isMuted,
            isPlaying: videoPlayer.isPlaying,
            currentResolution: videoPlayer.resolution,
            watchTimeLimitReached: videoPlayer.watchTimeLimitReached,
            virtualSlideIndex: this.videoSlideController.itemIndex,
            realSlideIndex: this.videoSlideController.realIndex,
            slidesCount: this.services.videoSlides.swiperController.getItemsCount(),
            currentFilter: this.services.videoSlides.currentAppliedFilter.id,
            ...eventData
        };

        if(this.videoType !== VideoTypeEnum.LiveStream) {
            eventData.videoDuration = this._roundTime(videoPlayer.videoDurationInSeconds);
        }

        return this.services.analytics.getLogger({
            videoSlides: {
                ...eventData
            }
        }).setCorrelationId(this.services.tools.getGuid())
            .setEventType('videoSlides', 'Log', 'Analytics');
    }

    reportPlayStarted(): void {
        this._createLogger({
            eventName: VideoSlidesAnalyticsEventEnum.PlayVideoStarted
        }).info();
    }

    reportRetryPlaySuccess() {
        this._createLogger({
            eventName: VideoSlidesAnalyticsEventEnum.RetryPlayVideoSuccess
        }).info();
    }

    reportResumePlaySuccess() {
        this._createLogger({
            eventName: VideoSlidesAnalyticsEventEnum.ResumePlayVideoSuccess
        }).info();
    }

    reportPauseOnAppSleep() {
        this._createLogger({
            eventName: VideoSlidesAnalyticsEventEnum.PauseVideoOnAppSleep
        }).info();
    }

    private _reportError(eventName: VideoSlidesAnalyticsEventEnum, error: any): void {
        this._createLogger({
            eventName: eventName,
            errorName: error.name ?? "",
            errorMessage: error.message ?? ""
        }).info();
    }

    reportPreloadError(error: any): void {
        this._reportError(VideoSlidesAnalyticsEventEnum.PreloadVideoError, error);
    }

    reportPlayError(error: any): void {
        this._reportError(VideoSlidesAnalyticsEventEnum.PlayVideoError, error);
    }

    reportRetryPlayError(error: any): void {
        this._reportError(VideoSlidesAnalyticsEventEnum.RetryPlayVideoError, error);
    }

    reportResumePlayError(error: any): void {
        this._reportError(VideoSlidesAnalyticsEventEnum.ResumePlayVideoError, error);
    }

    reportPosterLoadFailure(error: any): void {
        this._reportError(VideoSlidesAnalyticsEventEnum.VideoPosterLoadError, error);
    }

    reportDroppedFrames(droppedVideoFrames: number): void {
        if(droppedVideoFrames < 15 ) {
            return;
        }

        this._createLogger({
            eventName: VideoSlidesAnalyticsEventEnum.DroppedVideoFrames,
            droppedFrames: droppedVideoFrames
        }).info();
    }

    reportWatchTimeLimitReached(): void {
        this._createLogger({
            eventName: VideoSlidesAnalyticsEventEnum.VideoWatchTimeLimitReached,
        }).info();
    }

    reportSlideActivated(swipeDirection: SwipeDirectionEnum): void {
        this._createLogger({
            eventName: VideoSlidesAnalyticsEventEnum.SlideActivated,
            swipeDirection: swipeDirection
        }).info();
    }


    reportSlideDeactivated(params: {swipeDirection: SwipeDirectionEnum; slideActiveTime: TimeSpan}): void {
        this._createLogger({
            eventName: VideoSlidesAnalyticsEventEnum.SlideDeactivated,
            swipeDirection: params.swipeDirection,
            slideActiveTime: this._roundTime(params.slideActiveTime.totalSeconds)
        }).info();
    }

    reportVideoResolutionChanged(previousResolution: IVideoResolution, currentResolution: IVideoResolution): void {
        this._createLogger({
            eventName: VideoSlidesAnalyticsEventEnum.VideoResolutionChanged,
            previousResolution: previousResolution,
            currentResolution: currentResolution
        }).info();
    }

    reportBufferingTime(bufferingTime: TimeSpan): void {
        if(bufferingTime.totalSeconds > 1) {
            this._createLogger({
                eventName: VideoSlidesAnalyticsEventEnum.Buffering,
                bufferingTime: bufferingTime.totalSeconds
            }).info();
        }
    }

    reportPlayGame(): void {
        this._createLogger({
            eventName: VideoSlidesAnalyticsEventEnum.PlayGame
        }).info();
    }

    reportBookedGame(): void {
        this._createLogger({
            eventName: VideoSlidesAnalyticsEventEnum.BookGame
        }).info();
    }

    reportRemoveBookedGame(): void {
        this._createLogger({
            eventName: VideoSlidesAnalyticsEventEnum.RemoveBookedGame
        }).info();
    }

    reportMute() {
        this._createLogger({
            eventName: VideoSlidesAnalyticsEventEnum.Mute
        }).info();
    }

    reportUnmute() {
        this._createLogger({
            eventName: VideoSlidesAnalyticsEventEnum.Unmute
        }).info();
    }
}